import React, { useMemo, ReactElement } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { getAmountButtonTheme } from '../../style';
import { IDefaultButton, IView } from './types/OfferAmountButton';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import { CustomStyledProps } from 'style/style';
import breakpoints from 'theme/material/breakpoints';
import { isDarkMode } from 'utils/helpers';
import { FONT_FAMILIES } from 'theme/material/constants';

const DefaultButton = styled('div')<IDefaultButton>(
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  () => ({
    color: '#E2C92D !important',
    padding: 10,
    background: getAmountButtonTheme().backgroundStyle,
    borderRadius: '5px',
    width: 105,
    border: getAmountButtonTheme().borderStyle,
    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.16)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
    [breakpoints.down(768.05)]: {
      borderWidth: 2,
    },
  }),
  ({ viewType, type }: CustomStyledProps) =>
    viewType === 'grid'
      ? {
          margin: '5px 15px',
        }
      : {
          margin: '5px 10px',
          [breakpoints.down(639.05)]: {
            margin: type === 'sent' ? '5px 0px' : '5px 10px',
          },
          [breakpoints.down(426.05)]: {
            minWidth: 80,
            maxWidth: 80,
          },
          [breakpoints.down(361.05)]: {
            minWidth: 60,
            maxWidth: 80,
          },
          [breakpoints.down(320.05)]: {
            minWidth: 60,
            maxWidth: 80,
          },
        },

  ({ hasHoverEffect }: CustomStyledProps) =>
    hasHoverEffect && {
      '&:hover': {
        border: getAmountButtonTheme().borderHoverStyle,
        backgroundColor: '#2490DE',
        '> label': {
          color: '#fff',
        },
      },
    },
  ({ customBoxStyle }: CustomStyledProps) => ({ ...customBoxStyle })
);

const OfferLabel = styled('label')<IView>(
  () => ({
    color: isDarkMode() ? '#E2C92D' : '#8F8F8F',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '8px',
    letterSpacing: -0.3,
  }),
  ({ viewType }) =>
    viewType === 'list' && {
      [breakpoints.down(426.05)]: {
        fontSize: 10,
      },
    }
);

const Amount = styled('label')<IView>(
  () => ({
    fontFamily: FONT_FAMILIES.secondary,
    color: getAmountButtonTheme().color,
    fontSize: 45,
    lineHeight: '30px',
    letterSpacing: '-0.3px',
    [breakpoints.down(320.05)]: {
      marginBottom: 1,
    },
  }),
  ({ viewType }) =>
    viewType === 'list' && {
      [breakpoints.down(426.05)]: {
        fontSize: 23,
      },
    }
);

const EmptyAmount = styled('label')<IView>(
  (): CSSObject => ({
    color: getAmountButtonTheme().color,
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: '-0.02em',
    maxWidth: 83,
    textAlign: 'center',
  }),
  ({ viewType }) =>
    viewType === 'list' && {
      [breakpoints.down(426.05)]: {
        fontSize: 12,
      },
    }
);

interface OfferAmountButtonProps {
  price: string;
  type: string;
  hasHoverEffect: boolean;
  viewType: any;
  forceLabel?: string;
  onClick: () => void;
  customBoxStyle?: React.CSSProperties;
}

const OfferAmountButton = ({
  price,
  type,
  hasHoverEffect = false,
  forceLabel,
  ...rest
}: OfferAmountButtonProps): ReactElement => {
  const { isMobile } = useMediaBreakpoints();

  const receivedLabel = useMemo(() => (isMobile ? 'Suggest an Amount' : 'Asked You to Suggest an Amount'), [isMobile]);
  const style = useMemo(() => (hasHoverEffect ? { cursor: 'pointer' } : {}), [hasHoverEffect]);
  const renderLabel = type === 'received' ? receivedLabel : 'No Amount Suggested';

  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <DefaultButton style={style} className="offer-amount-button" {...rest} hasHoverEffect={hasHoverEffect} type={type}>
      {price !== '' ? (
        <>
          <Amount
            style={style}
            className="offer-amount-label"
            viewType={rest.viewType}
            data-test-id={'btn-amount-price'}
          >
            {price}
          </Amount>
          <OfferLabel style={style} className="offer-amount-label2" viewType={rest.viewType}>
            Offer Amount
          </OfferLabel>
        </>
      ) : (
        <EmptyAmount style={style} className="suggest-amount-label" viewType={rest.viewType}>
          {forceLabel ? forceLabel : renderLabel}
        </EmptyAmount>
      )}
    </DefaultButton>
  );
};

export default OfferAmountButton;
